import { useEffect, useId, useState } from "react";
import type { TaskCatalogItem } from "../../../../shared/types";
import { Button } from "../ui/Button";

type TaskLaunchDialogProps = {
  task: TaskCatalogItem | null;
  open: boolean;
  pending?: boolean;
  /** The Action cannot launch until a catalog-declared prerequisite is ready. */
  unavailable?: boolean;
  onCancel: () => void;
  onSubmit: (task: TaskCatalogItem, params: Record<string, unknown>) => Promise<void> | void;
  /** Setup routes render the exact same descriptor-driven form without modal chrome. */
  variant?: "dialog" | "page";
};

export function parseTaskLaunchParams(task: TaskCatalogItem, form: FormData): { params: Record<string, unknown> } | { error: string } {
  const launch = task.action.launch;
  if (!launch) return { params: {} };
  const params: Record<string, unknown> = {};
  for (const field of launch.fields) {
    if (field.type === "boolean") {
      const checked = form.get(field.key) === "on";
      if (field.required && !checked) return { error: `${field.label} must be confirmed before starting this Action.` };
      params[field.key] = checked;
      continue;
    }
    const raw = String(form.get(field.key) ?? "").trim();
    if (!raw) {
      if (field.required) return { error: `${field.label} is required before starting this Action.` };
      continue;
    }
    if (field.type === "number") {
      const value = Number(raw);
      if (!Number.isFinite(value)) return { error: `${field.label} must be a number.` };
      if (field.integer === true && !Number.isInteger(value)) return { error: `${field.label} must be a whole number.` };
      if (field.minimum !== undefined && value < field.minimum) return { error: `${field.label} must be at least ${field.minimum}.` };
      if (field.maximum !== undefined && value > field.maximum) return { error: `${field.label} must be no more than ${field.maximum}.` };
      params[field.key] = value;
      continue;
    }
    if (field.type === "select" && !field.options.some((option) => option.value === raw)) {
      return { error: `${field.label} must use one of the available options.` };
    }
    if (field.type !== "select" && field.max_length !== undefined && raw.length > field.max_length) {
      return { error: `${field.label} must be no more than ${field.max_length} characters.` };
    }
    params[field.key] = raw;
  }
  return { params };
}

export function TaskLaunchDialog({ task, open, pending = false, unavailable = false, onCancel, onSubmit, variant = "dialog" }: TaskLaunchDialogProps) {
  const titleId = useId();
  const [validationMessage, setValidationMessage] = useState<string | null>(null);
  const launch = task?.action.launch;

  useEffect(() => {
    if (!open) setValidationMessage(null);
  }, [open]);

  if (!open || !task || !launch) return null;
  const activeTask = task;
  const launchDisabled = pending || unavailable;

  async function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (pending || unavailable) return;
    const parsed = parseTaskLaunchParams(activeTask, new FormData(event.currentTarget));
    if ("error" in parsed) {
      setValidationMessage(parsed.error);
      return;
    }
    setValidationMessage(null);
    await onSubmit(activeTask, parsed.params);
  }

  const content = (
      <div aria-labelledby={titleId} aria-modal={variant === "dialog" ? "true" : undefined} className="w-full max-w-xl rounded-2xl bg-white p-6 shadow-xl" role={variant === "dialog" ? "dialog" : undefined}>
        <p className="font-mono text-[11px] uppercase tracking-[0.08em] text-brand-700">{activeTask.action.presentation.title}</p>
        <h2 id={titleId} className="mt-1 text-xl font-semibold text-slate-900">{launch.title}</h2>
        <p className="mt-2 text-sm leading-6 text-slate-600">{launch.description}</p>
        <form className="mt-5 space-y-4" onSubmit={(event) => void handleSubmit(event)}>
          {launch.fields.map((field, index) => {
            const fieldId = `${titleId}-${field.key}`;
            const required = field.required;
            const optional = !required && field.type !== "boolean";
            return (
              <label key={field.key} className="block" htmlFor={fieldId}>
                <span className="mb-2 block text-sm font-medium text-slate-800">
                  {field.label} {required ? <span aria-hidden="true">*</span> : optional ? <span className="font-normal text-slate-500">(optional)</span> : null}
                </span>
                {field.type === "textarea" ? (
                  <textarea autoFocus={index === 0} defaultValue={field.default} disabled={launchDisabled} id={fieldId} maxLength={field.max_length} name={field.key} rows={4} className="w-full rounded-xl border border-slate-300 px-3 py-2 text-sm text-slate-900 outline-none transition focus:border-brand-500 focus:ring-2 focus:ring-brand-100" />
                ) : field.type === "boolean" ? (
                  <input defaultChecked={field.default} disabled={launchDisabled} id={fieldId} name={field.key} type="checkbox" className="h-4 w-4 rounded border-slate-300 text-brand-600 focus:ring-brand-500" />
                ) : field.type === "select" ? (
                  <select defaultValue={field.default} disabled={launchDisabled} id={fieldId} name={field.key} className="w-full rounded-xl border border-slate-300 px-3 py-2 text-sm text-slate-900"><option value="">Select…</option>{field.options.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}</select>
                ) : (
                  <input autoFocus={index === 0} defaultValue={field.default} disabled={launchDisabled} id={fieldId} max={field.type === "number" ? field.maximum : field.max_length} min={field.type === "number" ? field.minimum : undefined} step={field.type === "number" && field.integer ? 1 : undefined} name={field.key} type={field.type === "number" ? "number" : "text"} className="w-full rounded-xl border border-slate-300 px-3 py-2 text-sm text-slate-900 outline-none transition focus:border-brand-500 focus:ring-2 focus:ring-brand-100" />
                )}
                <span className="mt-1 block text-xs leading-5 text-slate-500">{field.help}</span>
              </label>
            );
          })}
          {validationMessage ? <p className="text-sm text-red-700" role="alert">{validationMessage}</p> : null}
          <div className="flex justify-end gap-3 pt-1">
            <Button disabled={pending} onClick={onCancel} type="button" variant="secondary">Cancel</Button>
            <Button disabled={launchDisabled} type="submit">{pending ? "Starting…" : unavailable ? "Action unavailable" : launch.submit_label}</Button>
          </div>
        </form>
      </div>
  );
  if (variant === "page") return content;
  return <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/40 p-4" role="presentation">{content}</div>;
}
